import React, { useState, useCallback, useEffect } from "react";
import { CompactIntake } from "./components/CompactIntake";
import { SlideBrowser } from "./components/SlideBrowser";
import { ChatHelper } from "./components/ChatHelper";
import { DeckBuilder } from "./components/DeckBuilder";
import {
  api, ChatResponse, DeckBrief, IntakeData, ProjectSlide,
} from "./api/client";
const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const App: React.FC = () => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);

  // Intake
  const [intakeData, setIntakeData] = useState<IntakeData | null>(null);
  const [partialIntake, setPartialIntake] = useState<Partial<IntakeData> | null>(null);
  const [intakeCollapsed, setIntakeCollapsed] = useState(false);

  // Brief
  const [deckBrief, setDeckBrief] = useState<DeckBrief | null>(null);

  // Slides
  const [projectSlides, setProjectSlides] = useState<ProjectSlide[]>([]);

  // Build
  const [deckId, setDeckId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);

  // Chat
  const [chatExpanded, setChatExpanded] = useState(false);

  // Sync
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    api.newSession().then((d) => setSessionId(d.session_id));
    api.health().then((d) => setBackendVersion(d.version)).catch(() => {});
  }, []);

  // ---- Reset ----
  const handleStartAgain = useCallback(async () => {
    setMessages([]);
    setDeckBrief(null);
    setIntakeData(null);
    setPartialIntake(null);
    setIntakeCollapsed(false);
    setProjectSlides([]);
    setDeckId(null);
    setDownloadUrl(null);
    setChatExpanded(false);
    try {
      const d = await api.newSession();
      setSessionId(d.session_id);
    } catch {}
  }, []);

  // ---- Intake ----
  const handleIntakeComplete = useCallback(
    async (data: IntakeData) => {
      setIntakeData(data);
      setPartialIntake(data);
      setIntakeCollapsed(true);
      if (!sessionId) return;
      // Generate brief in background via chat
      setIsLoading(true);
      const greeting = `I need to build a deck for ${data.client_name}. Let's go.`;
      setMessages([{ role: "user", content: greeting }]);
      try {
        const response: ChatResponse = await api.sendMessage(
          sessionId, greeting, undefined, data
        );
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: response.reply },
        ]);
        if (response.deck_brief) setDeckBrief(response.deck_brief);
      } catch {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Sorry, something went wrong." },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId]
  );

  const handlePartialIntake = useCallback((partial: Partial<IntakeData>) => {
    setPartialIntake(partial);
  }, []);

  // ---- Chat ----
  const handleSend = useCallback(
    async (message: string) => {
      if (!sessionId) return;
      setMessages((prev) => [...prev, { role: "user", content: message }]);
      setIsLoading(true);
      try {
        const response: ChatResponse = await api.sendMessage(sessionId, message);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: response.reply },
        ]);
        if (response.deck_brief) setDeckBrief(response.deck_brief);
      } catch {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Sorry, something went wrong." },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId]
  );

  // ---- Slide selection ----
  const handleSlideSelect = useCallback((slide: ProjectSlide) => {
    setProjectSlides((prev) => {
      const idx = prev.findIndex(
        (s) => s.file_id === slide.file_id && s.slide_number === slide.slide_number
      );
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, slide];
    });
  }, []);

  const handleReorderSlides = useCallback((slides: ProjectSlide[]) => {
    setProjectSlides(slides);
  }, []);

  // ---- Build ----
  const handleBuild = useCallback(async () => {
    if (!sessionId || !deckBrief) return;
    setIsBuilding(true);
    try {
      const selected = projectSlides.map((s) => ({
        file_id: s.file_id,
        file_name: s.file_name,
        slide_number: s.slide_number,
      }));
      // Generate outline then build
      const outline = await api.generateOutline(sessionId, deckBrief, selected);
      const result = await api.buildFromOutline(
        sessionId, outline.outline_id, deckBrief, outline.slides, selected
      );
      setDeckId(result.deck_id);
      if (result.download_url) {
        setDownloadUrl(api.getDeckDownloadUrl(result.deck_id));
      }
    } catch (err) {
      console.error("Build failed:", err);
    } finally {
      setIsBuilding(false);
    }
  }, [sessionId, deckBrief, projectSlides]);

  const handleRevision = useCallback((newDeckId: string, newDownloadUrl: string) => {
    setDeckId(newDeckId);
    setDownloadUrl(newDownloadUrl);
  }, []);

  // ---- Sync ----
  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      await api.syncDrive();
    } catch {} finally {
      setIsSyncing(false);
    }
  }, []);

  return (
    <div style={styles.app}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerRow}>
          <div>
            <div style={styles.logo}>
              <span style={styles.logoBlack}>SITUATION</span>
              <span style={styles.logoRed}>PUBLISHING</span>
            </div>
            <h1 style={styles.title}>Sales Deck Builder</h1>
          </div>
          <div style={styles.headerButtons}>
            <button
              onClick={() => window.open(api.getAuthLoginUrl(), "_blank")}
              style={styles.headerBtn}
            >
              RE-AUTHORIZE
            </button>
            <button onClick={handleSync} style={styles.headerBtn} disabled={isSyncing}>
              {isSyncing ? "SYNCING..." : "SYNC LIBRARY"}
            </button>
            <button onClick={handleStartAgain} style={styles.headerBtn}>
              START AGAIN
            </button>
          </div>
        </div>
      </header>

      {/* Two-column layout */}
      <div style={styles.main}>
        {/* Left: Input / Browse */}
        <div style={styles.leftColumn}>
          <CompactIntake
            collapsed={intakeCollapsed}
            intakeData={intakeData}
            onComplete={handleIntakeComplete}
            onPartialUpdate={handlePartialIntake}
            onExpand={() => setIntakeCollapsed(false)}
          />
          <div style={styles.librarySection}>
            <SlideBrowser
              deckBrief={deckBrief}
              sessionId={sessionId}
              selectedSlides={projectSlides}
              onSlideSelect={handleSlideSelect}
            />
          </div>
          <ChatHelper
            messages={messages}
            onSend={handleSend}
            isLoading={isLoading}
            expanded={chatExpanded}
            onToggleExpand={() => setChatExpanded(!chatExpanded)}
          />
        </div>

        {/* Right: Deck Builder */}
        <div style={styles.rightColumn}>
          <DeckBuilder
            intakeData={partialIntake}
            deckBrief={deckBrief}
            isLoadingBrief={isLoading && !deckBrief}
            sessionId={sessionId}
            selectedSlides={projectSlides}
            onSlideSelect={handleSlideSelect}
            onReorderSlides={handleReorderSlides}
            onBuild={handleBuild}
            isBuilding={isBuilding}
            deckId={deckId}
            downloadUrl={downloadUrl}
            onRevision={handleRevision}
          />
        </div>
      </div>

      <footer style={styles.footer}>
        <span>UI v{APP_VERSION}</span>
        {backendVersion && <span style={styles.footerSep}>|</span>}
        {backendVersion && <span>API v{backendVersion}</span>}
      </footer>
    </div>
  );
};

const F = "'Roboto Mono', monospace";

const styles: Record<string, React.CSSProperties> = {
  app: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    fontFamily: F,
    fontSize: 11,
    color: "#1a1a1a",
    background: "#FAFAFA",
  },
  header: {
    padding: "16px 30px",
    borderBottom: "2px solid #E0E0E0",
    flexShrink: 0,
    background: "#FFFFFF",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logo: {
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: -0.5,
    marginBottom: 4,
  },
  logoBlack: { color: "#1a1a1a" },
  logoRed: { color: "#FF0000" },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: -0.5,
    color: "#1a1a1a",
  },
  headerButtons: {
    display: "flex",
    gap: 6,
  },
  headerBtn: {
    padding: "9px 18px",
    background: "#fff",
    color: "#1a1a1a",
    border: "1px solid #D0D0D0",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.5,
  },
  main: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  leftColumn: {
    width: "45%",
    minWidth: 320,
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid #E0E0E0",
    overflow: "hidden",
    background: "#FFFFFF",
  },
  librarySection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  rightColumn: {
    width: "55%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background: "#FFFFFF",
  },
  footer: {
    padding: "4px 30px",
    borderTop: "1px solid #E0E0E0",
    fontSize: 9,
    color: "#888",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    background: "#FFFFFF",
  },
  footerSep: {
    margin: "0 6px",
  },
};

export default App;
